import { Anchor, Box } from '@inflearn/ds-react-next';
import { useLang } from '@inflearn/i18n-tools';
import { ReviewItem, TranslateView } from '@inflearn/ui';
import { useTranslation } from 'next-i18next';
import { type ReactElement, useRef, useState } from 'react';

import { isEmpty } from 'lodash-es';
import type { ReviewUserProfileResponse } from '../../../../../resources/@types/uccSchema';
import { COURSE_URL, INFLEARN_URL } from '../../../../constants/urls';
import { useTrackMixpanelEvent } from '../../../../utils/GTM/useTrackMixpanelEvent';
import useCourseReviewIdOriginalLangContentQuery from '../hooks/query/useCourseReviewIdOriginalLangContentQuery';

type Props = {
  fullContent?: boolean;
} & ReviewUserProfileResponse;

export default function CourseReviewItem({ fullContent, ...review }: Props) {
  const { lang } = useLang();
  const { t } = useTranslation();
  const { trackMixpanelEvent } = useTrackMixpanelEvent();

  const handleClickCourseUrl = () => {
    trackMixpanelEvent('click_review_page', {
      click_spot: 'users',
      review_id: String(review.id),
      review_description: review.body.slice(0, 10),
      review_score: review.score,
      like_count: review.likeCount,
      review_user_name: review.writer.name,
      review_user_id: String(review.writer.id),
      course_identification: String(review.course.id),
      course_title: review.course.title,
    });
  };

  const isTranslated = review.originalLanguage !== review.contentLanguage;

  return (
    <ReviewItem
      lang={lang}
      title={review.course.title}
      content={
        <LazyLoadOriginalContent id={review.id} isTranslated={isTranslated}>
          {({ originalContent, isLoading }) => (
            <TranslateView.Root
              lang={lang}
              originalLang={review.originalLanguage}
              translatedLang={review.contentLanguage}
            >
              <Anchor
                size="md"
                c="gray.9"
                underline="never"
                lineClamp={fullContent ? Infinity : 3}
                onClick={handleClickCourseUrl}
                aria-label={t('수강평 상세페이지로 이동')}
                href={INFLEARN_URL.COMMUNITY_REVIEWS_REVIEW_ID(String(review.id))}
              >
                <TranslateView.Content
                  visibleSkeleton={isLoading}
                  translatedContent={review.body}
                  originalContent={originalContent}
                />
              </Anchor>
              {isEmpty(review.body) && isEmpty(originalContent) ? null : <TranslateView.Button />}
            </TranslateView.Root>
          )}
        </LazyLoadOriginalContent>
      }
      ratingCount={review.score}
      writtenDate={review.createdAt}
      writerName={review.writer?.name || ''}
      thumbnailUrl={review.course.thumbnailUrl}
      thumbnailSectionAnchorAttrs={{
        onClick: handleClickCourseUrl,
        'aria-label': t('강의 상세페이지로 이동'),
        href: COURSE_URL.COURSE_DETAIL(review.course.slug),
      }}
    />
  );
}

type LazyLoadOriginalContentProps = {
  id: number;
  isTranslated: boolean;
  children: (props: { isLoading: boolean; originalContent?: string }) => ReactElement;
};

function LazyLoadOriginalContent({ id, children, isTranslated }: LazyLoadOriginalContentProps) {
  const timeout = useRef<NodeJS.Timeout>();
  const isPreload = useRef(false);
  const [isHovered, setIsHovered] = useState(false);

  const { data, isInitialLoading } = useCourseReviewIdOriginalLangContentQuery({
    reviewId: String(id),
    options: {
      staleTime: 1_000 * 60,
      enabled: isTranslated && isHovered,
    },
  });

  const callOriginalContent = () => {
    if (!isTranslated) {
      return;
    }

    setIsHovered(true);
  };

  const handleMouseEnter = () => {
    if (!isTranslated) {
      return;
    }

    timeout.current = setTimeout(() => {
      isPreload.current = true;
      callOriginalContent();
    }, 1000);
  };

  const handleMouseLeave = () => {
    if (!timeout.current) {
      return;
    }
    clearTimeout(timeout.current);
  };

  return (
    <Box
      onClick={callOriginalContent}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children({
        originalContent: data?.content,
        isLoading: isInitialLoading && !isPreload.current,
      })}
    </Box>
  );
}
